import { addDays, parseISO } from "date-fns";

import type {
  FinanceCharge,
  FinanceContract,
  FinanceExpense,
} from "../../types";
import {
  buildChargesForMonth,
  getChargeStatus,
  roundMoney,
  toDateOnly,
  toMonth,
} from "./financeSchedule";

/** Days ahead a charge or an expense is reported as "due soon" */
export const FINANCE_DUE_SOON_DAYS = 7;

export type FinanceAlertKind =
  | "overdue"
  | "due_soon"
  | "not_generated"
  | "not_sent"
  | "paid_without_invoice"
  | "expenses_due"
  | "adjustment_due";

export type FinanceAlertSeverity = "error" | "warning" | "info";

export interface FinanceAlert {
  kind: FinanceAlertKind;
  severity: FinanceAlertSeverity;
  count: number;
  /** Total amount concerned, null when the alert is not about money */
  amount: number | null;
}

export interface FinanceAlertsInput {
  contracts: FinanceContract[];
  charges: FinanceCharge[];
  expenses: FinanceExpense[];
  today: Date;
}

const total = (records: { amount: number }[]) =>
  roundMoney(records.reduce((sum, record) => sum + record.amount, 0));

// The NF number is what the receivables "no invoice" filter can query
const hasInvoice = (charge: FinanceCharge) => Boolean(charge.invoice_number);

/** Whether a contract's yearly price adjustment falls in the current month */
export const isAdjustmentDue = (
  contract: FinanceContract,
  today: Date,
): boolean =>
  contract.status === "active" &&
  contract.adjustment_index !== "none" &&
  contract.adjustment_month === today.getMonth() + 1 &&
  // No adjustment during the first year
  parseISO(contract.start_date).getFullYear() < today.getFullYear();

/** Things the finance team should act on, the most urgent first, empty ones left out */
export const getFinanceAlerts = ({
  contracts,
  charges,
  expenses,
  today,
}: FinanceAlertsInput): FinanceAlert[] => {
  const todayDate = toDateOnly(today);
  const soonDate = toDateOnly(addDays(today, FINANCE_DUE_SOON_DAYS));
  const currentMonth = toMonth(today);
  const withStatus = charges.map((charge) => ({
    charge,
    status: getChargeStatus(charge, today),
  }));
  const pick = (predicate: (item: (typeof withStatus)[number]) => boolean) =>
    withStatus.filter(predicate).map(({ charge }) => charge);

  const overdue = pick(({ status }) => status === "overdue");
  const dueSoon = pick(
    ({ charge, status }) =>
      (status === "pending" || status === "sent") &&
      charge.due_date <= soonDate,
  );
  const notSent = pick(
    ({ charge, status }) =>
      status === "pending" && charge.competence_month <= currentMonth,
  );
  const paidWithoutInvoice = pick(
    ({ charge, status }) => status === "paid" && !hasInvoice(charge),
  );
  const notGenerated = buildChargesForMonth(contracts, charges, currentMonth);
  const expensesDue = expenses.filter(
    (expense) => !expense.paid_at && expense.due_date <= soonDate,
  );
  const adjustments = contracts.filter((contract) =>
    isAdjustmentDue(contract, today),
  );
  const hasLateExpense = expensesDue.some(
    (expense) => expense.due_date < todayDate,
  );

  const alerts: FinanceAlert[] = [
    {
      kind: "overdue",
      severity: "error",
      count: overdue.length,
      amount: total(overdue),
    },
    {
      kind: "expenses_due",
      severity: hasLateExpense ? "error" : "warning",
      count: expensesDue.length,
      amount: total(expensesDue),
    },
    {
      kind: "due_soon",
      severity: "warning",
      count: dueSoon.length,
      amount: total(dueSoon),
    },
    {
      kind: "not_generated",
      severity: "warning",
      count: notGenerated.length,
      amount: total(notGenerated),
    },
    {
      kind: "not_sent",
      severity: "warning",
      count: notSent.length,
      amount: total(notSent),
    },
    {
      kind: "paid_without_invoice",
      severity: "info",
      count: paidWithoutInvoice.length,
      amount: total(paidWithoutInvoice),
    },
    {
      kind: "adjustment_due",
      severity: "info",
      count: adjustments.length,
      amount: null,
    },
  ];
  return alerts.filter((alert) => alert.count > 0);
};
